import { useState } from "react";
import { Button, Icon, Input, Modal } from "@legal-500/legal500-design-system";
import { campaignsService, type Segment } from "@/api/campaigns.service";
import { AudienceCard } from "./AudienceCard";
import { buildReplaceTargetsBody, selectionCount, type RecipientSelection } from "./selection";
import styles from "./MailingGroups.module.css";

type Props = {
  campaignId: string;
  selection: RecipientSelection;
  templateName?: string | null;
  onClose: () => void;
  onCreated: (segment: Segment, count: number) => void;
};

// The browser's IANA zone is sent as a scheduling hint (arch-discussion §14.2);
// it is not a constraint — the backend stores and schedules the absolute UTC.
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function CreateSegmentModal({
  campaignId,
  selection,
  templateName,
  onClose,
  onCreated,
}: Props) {
  const [label, setLabel] = useState("");
  // Dispatch intent: send immediately (default) or schedule for a future instant.
  const [sendNow, setSendNow] = useState(true);
  const [scheduledAt, setScheduledAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = selectionCount(selection);

  // A future schedule requires a concrete instant: block an empty datetime and
  // any moment already in the past. Only enforced when "Schedule for later" is
  // chosen — for "Send now" the picker is irrelevant.
  const invalidEmpty = scheduledAt.trim() === "";
  const invalidPast = !invalidEmpty && new Date(scheduledAt).getTime() <= Date.now();
  const scheduleInvalid = !sendNow && (invalidEmpty || invalidPast);

  const handleSubmit = async () => {
    if (count === 0 || scheduleInvalid) return;
    setSubmitting(true);
    setError(null);

    try {
      const segment = await campaignsService.createSegment(campaignId, {
        ...(label.trim() ? { label: label.trim() } : {}),
      });

      let targetCount: number;
      try {
        targetCount = await campaignsService.replaceTargets(
          campaignId,
          segment.id,
          buildReplaceTargetsBody(selection),
        );
      } catch (targetErr) {
        // replaceTargets failed after the draft segment was created. Roll back
        // the orphan so a retry doesn't create a duplicate send; the segment is
        // a fresh draft with no targets, so delete is allowed.
        try {
          await campaignsService.deleteSegment(campaignId, segment.id);
        } catch (delErr) {
          console.error("Failed to roll back orphaned segment:", delErr);
        }
        // Surface the original replaceTargets error, not the rollback outcome.
        throw targetErr;
      }

      // Dispatch step: flip the draft straight to sending/scheduled so the
      // operator never sees a resting draft from this flow.
      //
      // Deliberately NOT wrapped in the rollback above: if scheduling fails the
      // segment already has frozen targets, and a draft-with-targets is fully
      // recoverable (the operator can retry, or Send now / Schedule it from the
      // Sends list). Deleting it here would throw away that frozen work, so we
      // surface the error inline and stop instead.
      const dispatched = await campaignsService.scheduleSegment(campaignId, segment.id, {
        scheduledAt: sendNow ? null : new Date(scheduledAt).toISOString(),
        timezone: BROWSER_TZ,
      });

      // Hand downstream the dispatched segment (with its updated status), not the
      // transient draft, so the toast/list reflect sending/scheduled.
      onCreated(dispatched, targetCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create send.");
      setSubmitting(false);
    }
  };

  const submitLabel = sendNow
    ? submitting
      ? "Sending…"
      : "Send now"
    : submitting
      ? "Scheduling…"
      : "Schedule";

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="New send"
      size="mediumLarge"
      blurBackground
      backdropClick={false}
    >
      <div className={styles.modalForm}>
        <AudienceCard selection={selection} onEdit={onClose} />

        {templateName && <p className={styles.modalIntro}>Template: {templateName}</p>}

        <div>
          <div className={styles.modalSectionLabel}>Label (optional)</div>
          <Input
            aria-label="Label (optional)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g. Spain 17:00 local"
            fullWidth
          />
        </div>

        <div>
          <div className={styles.modalSectionLabel}>When to send</div>
          <div className={styles.sendTypeOptions} role="radiogroup" aria-label="When to send">
            <label className={styles.sendTypeOption}>
              <input
                type="radio"
                name="send-type"
                checked={sendNow}
                onChange={() => setSendNow(true)}
              />
              <span>Send now</span>
            </label>
            <label className={styles.sendTypeOption}>
              <input
                type="radio"
                name="send-type"
                checked={!sendNow}
                onChange={() => setSendNow(false)}
              />
              <span>Schedule for later</span>
            </label>
          </div>

          {!sendNow && (
            <div className={styles.scheduleReveal}>
              <Input
                aria-label="Scheduled at"
                type="datetime-local"
                value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)}
                fullWidth
              />
              <p className={styles.tzHint}>
                Timezone hint: <code>{BROWSER_TZ}</code> — saved as absolute UTC.
              </p>
              {(invalidEmpty || invalidPast) && (
                <div className={styles.fieldError}>
                  <Icon icon="exclamation" size="small" />
                  <span>
                    {invalidEmpty
                      ? "Pick a date and time."
                      : "Choose a date and time in the future."}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className={styles.fieldError}>
            <Icon icon="exclamation" size="small" />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.sendModalFooter}>
          <span className={styles.footerCount}>
            Will be sent: <b>{count.toLocaleString()}</b>
          </span>
          <div className={styles.campaignsActions}>
            <Button variant="outlined" color="default" onClick={onClose} disabled={submitting}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting || count === 0 || scheduleInvalid}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
